import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowRight, Sparkles, Check, Phone, Mail, MessageCircle, ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import SEO from './SEO';
import { useT } from '../i18n';
import { resolveContent } from '../data/serviceContent';
import { detailFor } from '../data/serviceDetail';
import RelatedResources from './RelatedResources';
import FlagshipCard from './FlagshipCard';
import ContinueExploring from './ContinueExploring';
import HeroImage from './HeroImage';
import ServiceShowcase from './ServiceShowcases';
import { heroFor } from '../data/serviceImages';

/*
  ServiceLandingTemplate — premium long-form landing scaffold.
  Sections in order:
    1. Breadcrumb + Hero
    2. Pillars (3-up benefit grid)
    3. Use Cases (icon-led, 3-up)
    4. Implementation Process (4-step timeline)
    5. Outcomes (3-up KPI block, animated)
    6. Benefits checklist + Technology partners
    7. FAQs (accordion + JSON-LD schema)
    8. Related services
    9. CTA banner + quick contact
*/

// Resolve a lucide icon name -> React component, with safe fallback.
const Ico = ({ name, className = 'w-5 h-5' }) => {
  const Cmp = (name && Icons[name]) || Icons.Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

// Outcome headline variants — one per page (stable by slug) so the site never repeats itself.
const OUTCOME_HEADS = [
  { en: 'Measurable outcomes. No vague promises.', es: 'Resultados medibles. Sin promesas vagas.' },
  { en: 'Results you can see in your P&L.', es: 'Resultados que ves en tu P&L.' },
  { en: 'Numbers, not promises.', es: 'Números, no promesas.' },
  { en: 'What operators actually achieve with us.', es: 'Lo que los operadores logran con nosotros.' },
  { en: 'Impact you can measure from month one.', es: 'Impacto medible desde el primer mes.' },
  { en: 'Real results, from real restaurants.', es: 'Resultados reales, de restaurantes reales.' },
];
const slugHash = (s) => s.split('').reduce((a, ch) => a + ch.charCodeAt(0), 0) * 7 + s.length;

export default function ServiceLandingTemplate({ prefix = 'services' }) {
  const { slug = '' } = useParams();
  const { lang } = useT();
  const data = resolveContent(slug, prefix);
  const detail = detailFor(slug);

  if (!data) {
    return (
      <div className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-3xl font-bold text-neutral-900">Page not found</h1>
          <p className="mt-3 text-neutral-600">We could not resolve content for this page.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-[#F77F11] font-semibold">Back home <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    );
  }

  const t = data[lang] || data.en;
  const category = data.category[lang] || data.category.en;
  const isComingSoon = !!data.comingSoon;
  const isAi = !!data.ai || prefix === 'ai';
  const parentHref = isAi ? '/index-ai' : '/services';

  // Lift bilingual detail values
  const dv = (node) => (node && (node[lang] || node.en)) || '';
  const useCases = detail?.useCases || [];
  const process = detail?.process || [];
  const outcomes = detail?.outcomes || [];
  const deepDive = detail?.deepDive || [];
  const faqs = detail?.faqs || [];

  // -------- schema.org structured data (Service / FAQPage / BreadcrumbList / WebPage) --------
  const pageUrl = `https://www.kitxens.com/${prefix}/${slug}`;
  const description = `${t.tagline} ${t.intro}`.slice(0, 220);
  const graph = [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.kitxens.com/' },
        { '@type': 'ListItem', position: 2, name: isAi ? 'AI Solutions' : 'Services', item: `https://www.kitxens.com${parentHref}` },
        { '@type': 'ListItem', position: 3, name: t.title, item: pageUrl },
      ],
    },
    {
      '@type': 'Service',
      '@id': `${pageUrl}#service`,
      additionalType: isAi ? 'https://schema.org/SoftwareApplication' : undefined,
      name: t.title,
      serviceType: category,
      description,
      areaServed: ['United States', 'Canada', 'Mexico', 'Spain', 'Latin America'],
      provider: {
        '@type': 'Organization',
        name: 'Kitxens',
        url: 'https://www.kitxens.com',
      },
      url: pageUrl,
    },
    {
      '@type': 'WebPage',
      '@id': pageUrl,
      url: pageUrl,
      name: `${t.title} · Kitxens`,
      description,
      inLanguage: lang === 'es' ? 'es' : 'en',
      isPartOf: { '@type': 'WebSite', url: 'https://www.kitxens.com', name: 'Kitxens' },
    },
  ];

  if (faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: dv(f.q),
        acceptedAnswer: { '@type': 'Answer', text: dv(f.a) },
      })),
    });
  }

  const schema = { '@context': 'https://schema.org', '@graph': graph };

  return (
    <div className="bg-white" data-testid={`landing-${slug}`}>
      <SEO
        title={`${t.title} · Kitxens`}
        description={description}
        path={`/${prefix}/${slug}`}
        schema={schema}
      />

      {/* ------------ Hero ------------ */}
      <section className="pt-28 pb-12 lg:pt-32 lg:pb-16 bg-gradient-to-b from-orange-50/50 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          {(() => {
            const hero = heroFor(slug);
            const heroText = (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                <Link to={parentHref} className="inline-flex items-center gap-1 text-[12px] tracking-[0.18em] font-semibold uppercase text-[#F77F11] hover:text-[#d8651a]">
                  ← {category}
                </Link>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-neutral-900 leading-[1.05]">
                    {t.title}
                  </h1>
                  {isComingSoon && (
                    <span className="kx-eyebrow">
                      {lang === 'es' ? 'En fase final' : 'Being finalized'}
                    </span>
                  )}
                </div>
                <p className="mt-5 text-xl sm:text-2xl text-neutral-700 font-semibold leading-snug max-w-2xl">
                  {t.tagline}
                </p>
                <p className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl">
                  {t.intro}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/#audit"
                    className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all"
                    data-testid="landing-cta-audit"
                  >
                    {isAi
                      ? (lang === 'es' ? 'Solicita consultoría IA gratis' : 'Request Free AI Consultation')
                      : (lang === 'es' ? 'Solicita auditoría gratis' : 'Get a Free Audit')}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="inline-flex items-center gap-1 border border-neutral-300 hover:bg-neutral-50 text-neutral-800 h-12 px-7 rounded-full text-[15px] font-semibold"
                  >
                    {lang === 'es' ? 'Cómo funciona' : 'How It Works'} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            );

            if (!hero) {
              return heroText;
            }
            return (
              <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className="lg:col-span-7">{heroText}</div>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.1 }}
                  className="lg:col-span-5"
                >
                  <HeroImage image={hero} />
                </motion.div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ------------ Pillars ------------ */}
      {t.pillars && t.pillars.length > 0 && (
        <section className="py-16 lg:py-20">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {t.pillars.map((p, i) => (
                <motion.div
                  key={p.eyebrow}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  className="rounded-2xl border border-neutral-200 bg-white p-7 hover:border-orange-200 hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
                >
                  <Sparkles className="w-5 h-5 text-[#F77F11]" />
                  <p className="mt-3 text-[#F77F11] text-[11px] tracking-[0.2em] font-bold uppercase">{p.eyebrow}</p>
                  <p className="mt-2 text-neutral-800 font-semibold leading-snug text-[15.5px]">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------ Use Cases ------------ */}
      <ServiceShowcase slug={slug} lang={lang} />

      {useCases.length > 0 && (
        <section className="py-16 lg:py-20 bg-neutral-50/60" data-testid="landing-use-cases">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <p className="kx-eyebrow">
                {lang === 'es' ? 'Casos de uso' : 'Use cases'}
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
                {lang === 'es' ? 'Construido para tu operación real.' : 'Built for your real operation.'}
              </h2>
              <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
                {lang === 'es'
                  ? 'Estas son las situaciones concretas donde Kitxens entrega resultado desde la primera semana.'
                  : 'These are the concrete situations where Kitxens delivers results from week one.'}
              </p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {useCases.map((u, i) => (
                <motion.div
                  key={dv(u.t)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  whileHover={{ y: -3 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-7 hover:border-orange-200 hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                    <Ico name={u.i} className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                  </span>
                  <h3 className="mt-5 text-neutral-900 font-semibold text-[17px] leading-snug tracking-[-0.005em]">{dv(u.t)}</h3>
                  <p className="mt-2 text-neutral-600 text-[14.5px] leading-relaxed">{dv(u.b)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------ Process ------------ */}
      {process.length > 0 && (
        <section className="py-16 lg:py-20" data-testid="landing-process">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <p className="kx-eyebrow">
                {lang === 'es' ? 'Cómo trabajamos' : 'How we work'}
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
                {lang === 'es' ? <>Así llevamos <span className="text-[#F77F11]">{t.title}</span> a tu restaurante.</> : <>How we bring <span className="text-[#F77F11]">{t.title}</span> to your restaurant.</>}
              </h2>
              <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
                {lang === 'es'
                  ? 'Sin rehacer tu stack. Sin reemplazar tu equipo. Empezamos con lo que ya tienes y construimos desde ahí.'
                  : 'No rip-and-replace. No team disruption. We start with what you already run and build from there.'}
              </p>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 relative">
              {process.map((s, i) => (
                <motion.div
                  key={dv(s.t) + i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="relative rounded-2xl border border-neutral-200 bg-white p-6 hover:border-orange-200 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F77F11] text-white text-[13px] font-bold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {i < process.length - 1 && (
                      <ArrowRight className="hidden lg:block w-4 h-4 text-neutral-300" aria-hidden="true" />
                    )}
                  </div>
                  <h3 className="mt-5 text-neutral-900 font-semibold text-[17px] tracking-[-0.005em]">{dv(s.t)}</h3>
                  <p className="mt-2 text-neutral-600 text-[14.5px] leading-relaxed">{dv(s.b)}</p>
                </motion.div>
              ))}
            </div>

            {/* Process closing CTA — nothing here is a template */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45 }}
              className="mt-8 rounded-3xl border border-orange-200 bg-orange-50/60 p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6"
              data-testid="landing-process-cta"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[#F77F11] text-[10.5px] tracking-[0.22em] font-bold uppercase">
                  {lang === 'es' ? '100% adaptable a ti' : '100% adaptable to you'}
                </p>
                <h3 className="mt-2 text-xl sm:text-2xl font-bold text-neutral-900 tracking-[-0.01em]">
                  {lang === 'es' ? 'Nada de esto es plantilla.' : 'None of this is a template.'}
                </h3>
                <p className="mt-2 text-neutral-600 text-[14.5px] leading-relaxed max-w-xl">
                  {lang === 'es'
                    ? 'Cada paso se ajusta a tu operación, tu equipo y tu presupuesto. Empezamos por donde más te duele — y escalas solo cuando ves resultados.'
                    : 'Every step adapts to your operation, your team and your budget. We start where it hurts most — and you scale only when you see results.'}
                </p>
                <p className="mt-3 text-[13px] text-neutral-500">
                  {lang === 'es'
                    ? 'Un equipo completo de tecnología, marketing e IA — por menos de lo que cuesta un turno de mesero.'
                    : 'A full technology, marketing and AI team — for less than the cost of one server shift.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
                <a
                  href={`https://wa.me/14085901102?text=${encodeURIComponent(lang === 'es' ? `Hola Kitxens, quiero una llamada de descubrimiento sobre ${t.title}.` : `Hi Kitxens, I'd like a discovery call about ${t.title}.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb959] text-white h-12 px-6 rounded-full text-[14.5px] font-semibold shadow-sm transition-colors"
                  data-testid="landing-process-cta-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  {lang === 'es' ? 'Agendar llamada de descubrimiento' : 'Book a discovery call'}
                </a>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-1 border border-neutral-300 hover:border-[#F77F11] hover:text-[#F77F11] text-neutral-800 h-12 px-6 rounded-full text-[14.5px] font-semibold transition-colors"
                  data-testid="landing-process-cta-pricing"
                >
                  {lang === 'es' ? 'Planes desde $99/mes' : 'Plans from $99/mo'} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ------------ Outcomes / KPIs ------------ */}
      {outcomes.length > 0 && (
        <section className="py-16 lg:py-20 bg-[#0F1117] text-white" data-testid="landing-outcomes">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <p className="kx-eyebrow-dark">
                {lang === 'es' ? 'Resultados' : 'Outcomes'}
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight">
                {(() => { const h = OUTCOME_HEADS[slugHash(slug) % OUTCOME_HEADS.length]; return h[lang] || h.en; })()}
              </h2>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {outcomes.map((o, i) => (
                <motion.div
                  key={dv(o.k) + i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.06] hover:border-white/20 transition-all"
                >
                  <p className="text-[#F77F11] text-4xl sm:text-5xl font-bold tracking-[-0.02em] leading-none">
                    {dv(o.k)}
                  </p>
                  <p className="mt-4 text-neutral-300 text-[14.5px] leading-relaxed">{dv(o.v)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------ Deep Dive Chapters ------------ */}
      {deepDive.length > 0 && (
        <section className="py-16 lg:py-24 bg-white" data-testid="landing-deep-dive">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <div className="max-w-2xl mb-10">
              <p className="kx-eyebrow">
                {lang === 'es' ? 'Todo lo que gestionamos' : 'Everything we manage'}
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight tracking-[-0.02em]">
                {lang === 'es' ? 'Cada capítulo, operado con rigor.' : 'Every chapter, operated with rigor.'}
              </h2>
              <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
                {lang === 'es'
                  ? 'No es un servicio genérico. Es una operativa completa que gestiona cada palanca que mueve la aguja para tu restaurante.'
                  : 'This is not a generic service. It is a complete operating layer covering every lever that moves the needle for your restaurant.'}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {deepDive.map((d, i) => (
                <motion.div
                  key={dv(d.t) + i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.35)] transition-all"
                >
                  <span className="inline-flex w-10 h-10 rounded-lg bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                    <Ico name={d.i} className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                  </span>
                  <h3 className="mt-4 text-neutral-900 font-bold text-[16px] leading-snug tracking-[-0.005em]">
                    {dv(d.t)}
                  </h3>
                  <p className="mt-2 text-neutral-600 text-[13.5px] leading-relaxed">
                    {dv(d.b)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------ Benefits + Technologies ------------ */}
      {(t.benefits?.length > 0 || t.tech?.length > 0) && (
        <section className="py-16 lg:py-20 bg-neutral-50/60">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
            {t.benefits?.length > 0 && (
              <div>
                <p className="kx-eyebrow">{lang === 'es' ? 'Lo que vas a lograr' : "What you'll achieve"}</p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
                  {lang === 'es' ? 'Beneficios que se notan.' : 'Benefits you can feel.'}
                </h2>
                <ul className="mt-7 space-y-3">
                  {t.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-neutral-700">
                      <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-orange-50 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-[#F77F11]" />
                      </span>
                      <span className="text-base leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {t.tech?.length > 0 && (
              <div>
                <p className="kx-eyebrow">{lang === 'es' ? 'Tecnologías' : 'Technologies'}</p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
                  {lang === 'es' ? 'Con las plataformas que ya usas.' : 'With the platforms you already use.'}
                </h2>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {t.tech.map((name) => (
                    <span key={name} className="inline-flex items-center bg-white border border-neutral-200 rounded-full px-4 py-2 text-sm font-semibold text-neutral-700">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ------------ FAQs ------------ */}
      {faqs.length > 0 && (
        <section className="py-16 lg:py-24" data-testid="landing-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-center">
              <p className="kx-eyebrow">
                {lang === 'es' ? 'Preguntas frecuentes' : 'Frequently asked'}
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
                {lang === 'es' ? 'Lo que los operadores nos preguntan.' : 'What operators ask us.'}
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-10 w-full divide-y divide-neutral-200 border-y border-neutral-200">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={`faq-${i}`}
                  value={`faq-${i}`}
                  className="border-b-0"
                  data-testid={`landing-faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 text-[16px] font-semibold text-neutral-900 hover:no-underline hover:text-[#F77F11] text-left">
                    {dv(f.q)}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[15px] text-neutral-600 leading-relaxed">
                    {dv(f.a)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* ------------ Related Services ------------ */}
      {data.related?.length > 0 && (
        <section className="py-16 lg:py-20 bg-neutral-50/60">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <p className="kx-eyebrow">{lang === 'es' ? 'Servicios relacionados' : 'Related services'}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
              {lang === 'es' ? 'Explora la plataforma Kitxens.' : 'Explore the Kitxens platform.'}
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.related.map((r) => (
                <Link
                  key={r.href}
                  to={r.href}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-4 hover:border-[#F77F11] hover:bg-orange-50/40 transition-all"
                  data-testid={`landing-related-${r.href.replace(/[^a-z0-9]+/gi, '-').replace(/(^-|-$)/g, '')}`}
                >
                  <span className="font-semibold text-neutral-900 text-[15px] group-hover:text-[#F77F11] transition-colors">{r.label}</span>
                  <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[#F77F11] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------ Related Resources (Magazine) ------------ */}
      <RelatedResources serviceSlug={slug} lang={lang} />

      {/* ------------ Contextual flagship micro-promotion ------------ */}
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-6">
        <FlagshipCard context={slug} />
      </div>

      {/* ------------ CTA Banner ------------ */}
      <ContinueExploring context={slug} />
      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-[#F77F11] text-white p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-5 overflow-hidden relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.10]"
              style={{ backgroundImage: 'radial-gradient(circle at 25% 30%, #fff 1px, transparent 1.5px), radial-gradient(circle at 75% 70%, #fff 1px, transparent 1.5px)', backgroundSize: '110px 110px, 150px 150px' }}
            />
            <div className="relative">
              <p className="text-white/80 text-xs tracking-[0.22em] font-semibold uppercase">
                {isAi ? (lang === 'es' ? 'Consultoría IA Gratis' : 'Free AI Consultation') : (lang === 'es' ? 'Evaluación Gratis' : 'Free Assessment')}
              </p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-bold leading-tight max-w-xl">
                {isAi
                  ? (lang === 'es' ? '¿Listo para activar IA en tu restaurante?' : 'Ready to activate AI in your restaurant?')
                  : (lang === 'es' ? '¿Listo para crecer con Kitxens?' : 'Ready to grow with Kitxens?')}
              </h3>
            </div>
            <div className="relative flex flex-wrap gap-3">
              <Link to="/#audit" className="inline-flex items-center gap-1 bg-white text-[#F77F11] hover:bg-neutral-100 h-12 px-6 rounded-full text-sm font-semibold" data-testid="landing-footer-cta-audit">
                {lang === 'es' ? 'Solicitar Auditoría' : 'Request Free Audit'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact#contact-form" className="inline-flex items-center gap-1 bg-white/0 border border-white/40 hover:bg-white/10 text-white h-12 px-6 rounded-full text-sm font-semibold" data-testid="landing-footer-cta-talk">
                {lang === 'es' ? 'Hablar con un experto' : 'Talk to an expert'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Quick contact */}
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-neutral-600">
            <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 hover:text-[#F77F11]"><MessageCircle className="w-4 h-4 text-[#25D366] group-hover:text-[#F77F11] transition-colors" /> WhatsApp <span className="text-[11px] text-neutral-400 group-hover:text-[#F77F11]/80 transition-colors">(ESP | ENG)</span></a>
            <a href="tel:+14155982310" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Phone className="w-4 h-4 text-[#F77F11]" /> +1 (415) 598-2310</a>
            <a href="mailto:hello@kitxens.com" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Mail className="w-4 h-4 text-[#F77F11]" /> hello@kitxens.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
